import React from 'react';
import './Table.css';

export interface TableColumn<T = any> {
  key: string;
  header: string | React.ReactNode;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  width?: string | number;
}

export interface TableProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor?: (row: T, index: number) => string | number;
  onRowClick?: (row: T, index: number) => void;
  emptyMessage?: string;
  className?: string;
  variant?: 'default' | 'compact' | 'bordered';
  hoverable?: boolean;
  striped?: boolean;
}

/**
 * Reusable Table component with consistent styling
 */
function Table<T = any>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  emptyMessage = 'No data available',
  className = '',
  variant = 'default',
  hoverable = true,
  striped = false
}: TableProps<T>) {
  const getRowKey = (row: T, index: number): string | number => {
    if (keyExtractor) {
      return keyExtractor(row, index);
    }
    // Try to find common id fields
    if (row && typeof row === 'object' && 'id' in row) {
      return (row as any).id;
    }
    if (row && typeof row === 'object' && 'key' in row) {
      return (row as any).key;
    }
    return index;
  };

  const variantClass = `common-table--${variant}`;
  const hoverableClass = hoverable ? 'common-table--hoverable' : '';
  const stripedClass = striped ? 'common-table--striped' : '';
  const tableClassName = className 
    ? `common-table ${variantClass} ${hoverableClass} ${stripedClass} ${className}`.trim()
    : `common-table ${variantClass} ${hoverableClass} ${stripedClass}`.trim();

  const handleRowClick = (row: T, index: number) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  if (data.length === 0) {
    return (
      <div className="common-table-container">
        <div className="common-table-empty">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="common-table-container">
      <div className="common-table-wrapper">
        <table className={tableClassName}>
          <thead>
            <tr>
              {columns.map((column) => {
                const alignClass = column.align ? `common-table-cell--${column.align}` : '';
                const style = column.width ? { width: column.width } : undefined;
                
                return (
                  <th 
                    key={column.key} 
                    className={alignClass}
                    style={style}
                  >
                    {column.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => {
              const rowKey = getRowKey(row, rowIndex);
              const clickable = onRowClick ? 'common-table-row--clickable' : '';
              
              return (
                <tr 
                  key={rowKey}
                  className={clickable}
                  onClick={() => handleRowClick(row, rowIndex)}
                >
                  {columns.map((column) => {
                    const cellValue = (row as any)[column.key];
                    const alignClass = column.align ? `common-table-cell--${column.align}` : '';
                    
                    return (
                      <td key={column.key} className={alignClass}>
                        {column.render 
                          ? column.render(cellValue, row, rowIndex)
                          : cellValue != null ? String(cellValue) : '—'
                        }
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Table;

